import { themes } from '../themes';

/**
 * Node colours for the Journeys Sankey.
 *
 * A node's colour is its identity: the hash picks a hue inside its type's range,
 * so the same node is the same colour wherever it appears. Only how light that
 * hue is rendered depends on the theme.
 */

export const TYPE_HUE_RANGES = {
  AGENT: [0, 60],        // Warm: reds, oranges, yellows
  CHAIN: [330, 360],     // Warm-magenta: a step that is not typed as an agent
  LLM: [270, 330],       // Cool: purples, magentas — same family as MODEL
  TOOL: [30, 90],        // Warm-neutral: oranges, yellows, yellow-greens
  MODEL: [270, 330],     // Cool: purples, magentas
  OPERATION: [210, 270], // Cool-neutral: blues, indigos for framework/runtime steps
  RETRIEVER: [180, 240], // Cool: cyans, blues
  RERANKER: [150, 180],  // Cool: teals, sitting beside RETRIEVER
  EMBEDDING: [240, 270], // Cool: blues, indigos, sitting beside RETRIEVER
  MCP: [90, 150],        // Neutral: yellow-greens, greens
  SERVICE: [150, 210],   // Cool-neutral: greens, teals, cyans
  WORKFLOW: [200, 220],  // Teal-cyan for business task grouping
  OUTCOME: [280, 310],   // Purple-magenta for outcomes
};

/**
 * Simple string hash function
 */
export function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Convert HSL to RGB
 */
function hslToRgb(h, s, l) {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

/**
 * Convert RGB to hex
 */
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/** Relative luminance, the WCAG definition, for a 0-255 channel triple. */
function relativeLuminance({ r, g, b }) {
  const channel = (value) => {
    const c = value / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastAgainst(rgb, backgroundLuminance) {
  const l = relativeLuminance(rgb);
  const [hi, lo] = l > backgroundLuminance ? [l, backgroundLuminance] : [backgroundLuminance, l];
  return (hi + 0.05) / (lo + 0.05);
}

const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
  return { r, g, b };
};

// The light canvas, as a luminance: nodes are solid fills against it, so this is
// what they have to stand out from. Read from the theme rather than written
// down, so moving the canvas moves this.
const CANVAS_LUMINANCE = {
  light: relativeLuminance(hexToRgb(themes.light.colors.canvas)),
};

// What a node fill should reach against its canvas. Not the 4.5 of body text:
// these are large blocks of colour, and pushing every hue that far flattens the
// palette into mud. 3 is the WCAG floor for a graphical object.
const TARGET_CONTRAST = 3.2;

/**
 * Lightness that lifts this hue clear of the light canvas.
 *
 * A fixed band cannot do this. Equal HSL lightness is not equal perceived
 * luminance: at L=38 a yellow-green is far brighter than a blue, so any single
 * band leaves the green family washed out while the blues go heavy. Searching
 * down per hue gives every node type the same legibility.
 *
 * Only the light theme needs it. The dark canvas is dark enough that the
 * original band already clears the target by a wide margin, so searching there
 * would only drag every fill darker than it has been.
 */
function lightnessForLightCanvas(hue, saturation) {
  let best = 75;
  for (let lightness = 75; lightness >= 20; lightness -= 1) {
    best = lightness;
    if (contrastAgainst(hslToRgb(hue, saturation, lightness), CANVAS_LUMINANCE.light) >= TARGET_CONTRAST) {
      break;
    }
  }
  return best;
}

/**
 * Get color from type family using hash-based selection
 *
 * Each type gets a hue range and the hash picks a hue within it, so a node keeps
 * its identity. Only the lightness changes with the theme: the band was tuned
 * for a dark canvas, and on a light one those fills measured 1.0 to 1.9 against
 * the background, with the yellow-greens sitting almost exactly on it.
 */
export function getColorFromTypeFamily(nodeName, nodeType, isDark = true) {
  const hash = hashString(nodeName);

  const [minHue, maxHue] = TYPE_HUE_RANGES[nodeType] || [0, 360];
  const hue = minHue + (hash % (maxHue - minHue));

  // Dark keeps the band it always had: it reads well and nothing about it is
  // the problem. Light drops the saturation, because 80-95 reads as neon on a
  // pale canvas, and takes the lightness the hue actually needs.
  const saturation = isDark ? 80 + (hash % 16) : 55 + (hash % 16);
  const lightness = isDark
    ? 60 + (hash % 21)
    : lightnessForLightCanvas(hue, saturation);

  const rgb = hslToRgb(hue, saturation, lightness);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}
